import { FC } from 'react';

import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCoupon,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Image } from '@shared/api/media/media.interface';
import {
  giftCouponRefundRecipientUrl,
  giftCouponRenewalRecipientUrl,
} from '@shared/utils/giftRecipientLink.util';

import { formatPhone } from '../../utils/couponSearch.utils';
import { TabType } from './types';
import {
  formatDate,
  getIssuedByName,
  getItemName,
  getItemPrice,
  getOrderInfo,
} from './utils';

interface IssuedCouponRowProps {
  coupon: GiftCoupon;
  activeTab: TabType;
  isProcessing: boolean;
  onCancelIssue: (couponId: string) => void;
  onApprove: (coupon: GiftCoupon, type: TabType) => void;
  /** 발행된 환불 링크를 안내 문자로 발송 (Admin·GiftAgent — 없으면 버튼 숨김) */
  onSendRefundNotice?: (coupon: GiftCoupon) => void;
  onViewSignature: (image: Image) => void;
  onViewRenewalCoupon: (coupon: GiftCoupon) => void;
  showToast: (msg: string) => void;
}

export const IssuedCouponRow: FC<IssuedCouponRowProps> = ({
  coupon,
  activeTab,
  isProcessing,
  onCancelIssue,
  onApprove,
  onSendRefundNotice,
  onViewSignature,
  onViewRenewalCoupon,
  showToast,
}) => {
  const issued = coupon.issued;
  if (!issued) return null;

  const hasRequested = !!issued.requestedAt;
  const isApproved =
    issued.type === 'refund' ? !!coupon.refund?.refundedAt : coupon.isRenewed === true;

  return (
    <tr className={isApproved ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
      {/* 상태 */}
      <td className="whitespace-nowrap px-4 py-3">
        {isApproved ? (
          <div>
            <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
              승인완료
            </span>
            {issued.type === 'refund' && coupon.refund?.refundedAt && (
              <p className="mt-0.5 text-[12px] text-gray-500">
                {formatDate(coupon.refund.refundedAt)}
              </p>
            )}
            {issued.type === 'renewal' &&
              (coupon.renewal?.renewedAt ?? coupon.renewalCoupon?.createdAt) && (
                <p className="mt-0.5 text-[12px] text-gray-500">
                  {formatDate(coupon.renewal?.renewedAt ?? coupon.renewalCoupon?.createdAt)}
                  {coupon.renewal?.amount != null &&
                    ` · ${coupon.renewal.amount.toLocaleString()}원`}
                </p>
              )}
          </div>
        ) : hasRequested ? (
          <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
            승인대기
          </span>
        ) : (
          <span className="inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-700">
            미신청
          </span>
        )}
      </td>

      {/* 쿠폰코드 */}
      <td className="whitespace-nowrap px-4 py-3">
        <button
          onClick={() => {
            const url =
              issued.type === 'refund'
                ? giftCouponRefundRecipientUrl(coupon._id)
                : giftCouponRenewalRecipientUrl(coupon._id);
            navigator.clipboard.writeText(url);
            showToast('고객 URL이 복사되었습니다.');
          }}
          className="group text-left"
          title="클릭하면 고객 URL이 복사됩니다"
        >
          <div className="text-xs font-bold text-indigo-600 underline decoration-dotted underline-offset-2 group-hover:text-indigo-800">
            #{coupon.seq ?? ''} / {coupon.code}
          </div>
          <div className="font-mono text-[12px] text-gray-500 underline decoration-dotted underline-offset-2 group-hover:text-indigo-500">
            {coupon._id}
          </div>
        </button>
      </td>

      {/* 상품 */}
      <td className="px-4 py-3">
        <div className="text-xs font-medium text-gray-800">{getItemName(coupon)}</div>
        {getItemPrice(coupon) > 0 && (
          <div className="text-[12px] text-gray-600">
            {getItemPrice(coupon).toLocaleString()}원
          </div>
        )}
      </td>

      {/* 신청자 */}
      <td className="px-4 py-3 text-xs text-gray-600">
        {coupon.recipient ? (
          <div className="space-y-0.5">
            <p className="font-medium text-gray-700">{coupon.recipient.name}</p>
            <p className="text-[12px] text-gray-500">
              {formatPhone(coupon.recipient.phoneNumber)}
            </p>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </td>

      {/* 쿠폰상태 */}
      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
        <div className="space-y-0.5">
          <p>{coupon.redeemStatus}</p>
          {coupon.gifticon?.deliveryChannel && (
            <p className="text-[12px] text-gray-600">
              {
                DELIVERY_CHANNEL_CONFIG[
                  coupon.gifticon.deliveryChannel as DeliveryChannel
                ]?.label
              }
            </p>
          )}
        </div>
      </td>

      {/* 주문자 */}
      <td className="px-4 py-3 text-xs text-gray-600">
        {(() => {
          const orderInfo = getOrderInfo(coupon);
          if (!orderInfo) return <span className="text-gray-500">-</span>;
          return (
            <div className="space-y-0.5">
              <p className="font-medium text-gray-700">{orderInfo.name}</p>
              <p className="text-[12px] text-gray-500">{formatPhone(orderInfo.phone)}</p>
            </div>
          );
        })()}
      </td>

      {/* 이슈시작 */}
      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
        <div className="space-y-0.5">
          <p className="font-medium text-gray-700">{getIssuedByName(issued.issuedBy)}</p>
          <p className="text-[12px] text-gray-500">{formatDate(issued.issuedAt)}</p>
          {issued.noticeSentAt && (
            <p className="text-[12px] text-orange-600">
              문자 {issued.noticeCount ?? 1}회 · {formatPhone(issued.noticePhone)} ·{' '}
              {formatDate(issued.noticeSentAt)}
            </p>
          )}
        </div>
      </td>

      {/* 고객 신청일 */}
      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
        {issued.requestedAt ? (
          formatDate(issued.requestedAt)
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </td>

      {/* 신청정보 */}
      <td className="px-4 py-3 text-xs text-gray-600">
        {coupon.refund && hasRequested ? (
          <div className="space-y-0.5">
            {activeTab === 'refund' && coupon.refund.bank && (
              <p>
                {coupon.refund.bank} {coupon.refund.accountNumber}
              </p>
            )}
            {activeTab === 'refund' && coupon.refund.accountHolder && (
              <p className="text-gray-500">예금주: {coupon.refund.accountHolder}</p>
            )}
            {coupon.refund.phoneNumber && (
              <p className="text-gray-500">전화: {coupon.refund.phoneNumber}</p>
            )}
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </td>

      {/* 서명 */}
      <td className="whitespace-nowrap px-4 py-3">
        {coupon.refund?.signatureImage ? (
          <button
            onClick={() => {
              const img = coupon.refund?.signatureImage;
              if (img) onViewSignature(img as Image);
            }}
            className="rounded bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-100"
          >
            서명보기
          </button>
        ) : (
          <span className="text-xs text-gray-500">-</span>
        )}
      </td>

      {/* 액션 */}
      <td className="whitespace-nowrap px-4 py-3">
        <div className="flex items-center gap-1.5">
          {onSendRefundNotice &&
            issued.type === 'refund' &&
            !isApproved &&
            !hasRequested && (
              <button
                onClick={() => onSendRefundNotice(coupon)}
                disabled={isProcessing}
                className="rounded bg-orange-50 px-2.5 py-1 text-[11px] font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
              >
                {issued.noticeSentAt ? '문자 재발송' : '문자 발송'}
              </button>
            )}
          {!isApproved && !hasRequested && (
            <button
              onClick={() => onCancelIssue(coupon._id)}
              disabled={isProcessing}
              className="rounded bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {isProcessing ? '...' : '링크취소'}
            </button>
          )}
          {!isApproved && hasRequested && (
            <button
              onClick={() => onApprove(coupon, activeTab)}
              disabled={isProcessing}
              className={`rounded px-2.5 py-1 text-[11px] font-bold ${
                activeTab === 'refund'
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-violet-600 text-white hover:bg-violet-700'
              } disabled:opacity-50`}
            >
              {isProcessing ? '처리중...' : '승인처리'}
            </button>
          )}
          {coupon.renewalCoupon && (
            <button
              onClick={() => {
                if (coupon.renewalCoupon) onViewRenewalCoupon(coupon.renewalCoupon);
              }}
              className="rounded bg-violet-50 px-2.5 py-1 text-[11px] font-medium text-violet-600 hover:bg-violet-100"
            >
              연장쿠폰보기
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
